import { useEffect, useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

const DURATION_MS = 220

/**
 * Overlay modal with fade/scale enter+exit so open/close doesn’t jump.
 */
export function FadeModal({
  open,
  onClose,
  labelledBy,
  className,
  panelClassName,
  children,
}: {
  open: boolean
  onClose: () => void
  labelledBy?: string
  className?: string
  panelClassName?: string
  children: ReactNode
}) {
  const [mounted, setMounted] = useState(open)
  const [entered, setEntered] = useState(false)

  useEffect(() => {
    if (open) {
      setMounted(true)
      const id = window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => setEntered(true))
      })
      return () => window.cancelAnimationFrame(id)
    }
    setEntered(false)
    const timer = window.setTimeout(() => setMounted(false), DURATION_MS)
    return () => window.clearTimeout(timer)
  }, [open])

  useEffect(() => {
    if (!mounted) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [mounted, onClose])

  if (!mounted) return null

  return (
    <div
      className={cn(
        'fixed inset-0 z-[60] flex items-center justify-center bg-navy/70 p-4 backdrop-blur-sm transition-opacity duration-200 ease-out',
        entered ? 'opacity-100' : 'opacity-0',
        className,
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        className={cn(
          'w-full max-w-md transition-all duration-200 ease-out',
          entered ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-1 scale-[0.98] opacity-0',
          panelClassName,
        )}
      >
        {children}
      </div>
    </div>
  )
}
